import React, { useState, useEffect } from 'react';
import { AlertCircle, ChefHat, Loader, CheckCircle, Clock } from 'lucide-react';

interface AdminOTPFormProps {
  email: string;
  onVerify: () => void;
  onCancel: () => void;
  sessionWarning?: string | null;
}

export function AdminOTPForm({ email, onVerify, onCancel, sessionWarning }: AdminOTPFormProps) {
  const [otp, setOtp] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [remainingTime, setRemainingTime] = useState(120); // 2 minutes countdown
  const [expectedOtp, setExpectedOtp] = useState<string | null>(null);

  // Generate and send OTP when component mounts
  useEffect(() => {
    sendOTP();
    
    // Set up countdown timer
    const timer = setInterval(() => {
      setRemainingTime((prevTime) => {
        if (prevTime <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Format remaining time as MM:SS
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Generate a 6-digit OTP code
  const generateOTP = () => {
    const digits = '0123456789';
    let OTP = '';
    for (let i = 0; i < 6; i++) {
      OTP += digits[Math.floor(Math.random() * 10)];
    }
    return OTP;
  };

  // Send OTP via email
  const sendOTP = async () => {
    setIsResending(true);
    setError(null);
    
    try {
      const newOTP = generateOTP();
      setExpectedOtp(newOTP);
      
      // Send email with OTP
      const emailContent = `
        <!DOCTYPE html>
        <html>
          <head>
            <style>
              .container {
                font-family: Arial, sans-serif;
                max-width: 600px;
                margin: 0 auto;
                padding: 20px;
                border: 1px solid #e5e7eb;
                border-radius: 8px;
              }
              .header {
                background-color: #4CAF50;
                color: white;
                padding: 10px;
                text-align: center;
                border-radius: 8px 8px 0 0;
              }
              .content {
                padding: 20px;
              }
              .otp {
                font-size: 24px;
                font-weight: bold;
                text-align: center;
                letter-spacing: 8px;
                margin: 20px 0;
                padding: 10px;
                background-color: #f3f4f6;
                border-radius: 4px;
              }
              .footer {
                font-size: 12px;
                color: #6b7280;
                text-align: center;
                margin-top: 20px;
              }
              .warning {
                background-color: #fef3c7;
                border: 1px solid #f59e0b;
                color: #92400e;
                padding: 10px;
                border-radius: 4px;
                margin: 10px 0;
              }
            </style>
          </head>
          <body>
            <div class="container">
              <div class="header">
                <h2>Menioo Admin Authentication</h2>
              </div>
              <div class="content">
                <p>Hello Admin,</p>
                <p>Your one-time password for Menioo Admin access is:</p>
                <div class="otp">${newOTP}</div>
                <div class="warning">
                  <strong>Security Notice:</strong> This code will expire in 2 minutes. For your security, this verification will also expire after 2 hours of being granted.
                </div>
                <p>Do not share this code with anyone.</p>
              </div>
              <div class="footer">
                <p>This is an automated message, please do not reply.</p>
                <p>&copy; ${new Date().getFullYear()} Menioo Admin</p>
              </div>
            </div>
          </body>
        </html>
      `;

      const plainText = `
        Hello Admin,
        
        Your one-time password for Menioo Admin access is: ${newOTP}
        
        SECURITY NOTICE: This code will expire in 2 minutes. For your security, this verification will also expire after 2 hours of being granted.
        
        Do not share this code with anyone.
        
        This is an automated message, please do not reply.
        
        © ${new Date().getFullYear()} Menioo Admin
      `;

      const response = await fetch('https://menioo-backend-production.up.railway.app/api/email/send', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          to: email,
          subject: "Menioo Admin - Verification Code",
          html: emailContent,
          text: plainText,
          from: {
            email: "admin@menioo.com",
            name: "Menioo Admin"
          }
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to send OTP');
      }

      // Reset timer if resending
      setRemainingTime(120);
    } catch (error) {
      console.error('Error sending OTP:', error);
      setError('Failed to send verification code. Please try again.');
    } finally {
      setIsResending(false);
    }
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      // If OTP matches
      if (otp === expectedOtp) {
        setSuccess(true);
        // Delay to show success message
        setTimeout(() => {
          onVerify();
        }, 1500);
      } else {
        setError('Invalid verification code. Please try again.');
      }
    } catch (error) {
      console.error('Error verifying OTP:', error);
      setError('Failed to verify code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // If timer expires, show resend button
  const timerExpired = remainingTime === 0;

  if (success) {
    return (
      <div className="min-h-screen bg-menioo-cream flex flex-col items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-md p-8 max-w-md w-full">
          <div className="flex justify-center mb-6">
            <div className="rounded-full bg-menioo-green/10 p-4">
              <CheckCircle className="h-12 w-12 text-menioo-green" />
            </div>
          </div>
          <h2 className="text-2xl font-display font-bold text-menioo-navy text-center mb-4">
            Verification Successful
          </h2>
          <p className="text-gray-600 text-center mb-6">
            You are being redirected to the admin dashboard...
          </p>
          <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-md text-sm mb-4">
            <div className="flex items-center">
              <Clock className="h-4 w-4 mr-2 flex-shrink-0" />
              <span>Your session will automatically expire after 1 hour of inactivity, and this verification will expire after 2 hours.</span>
            </div>
          </div>
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-menioo-green"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-menioo-cream flex flex-col items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-md p-8 max-w-md w-full">
        <div className="flex justify-center mb-6">
          <div className="rounded-full bg-white p-3 shadow-md">
            <ChefHat className="h-12 w-12 text-menioo-green" />
          </div>
        </div>
        <h2 className="text-2xl font-display font-bold text-menioo-navy text-center mb-4">
          Verify Your Email
        </h2>
        <p className="text-gray-600 text-center mb-6">
          We've sent a verification code to <strong>{email}</strong>. 
          Please enter the code below to continue.
        </p>

        {sessionWarning && (
          <div className="mb-4 bg-amber-50 border border-amber-200 text-amber-700 px-4 py-3 rounded relative flex items-center">
            <Clock className="h-5 w-5 mr-2 flex-shrink-0" />
            <span className="text-sm">{sessionWarning}</span>
          </div>
        )}
        
        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative flex items-center">
            <AlertCircle className="h-5 w-5 mr-2" />
            <span>{error}</span>
          </div>
        )}

        {/* Security Notice */}
        <div className="mb-4 bg-blue-50 border border-blue-200 text-blue-800 px-4 py-3 rounded-md text-sm">
          <div className="flex items-start">
            <Clock className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium">Security Notice:</p>
              <p>• Session expires after 1 hour of inactivity</p>
              <p>• Verification expires after 2 hours</p>
            </div>
          </div>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="otp" className="block text-sm font-medium text-gray-700 mb-1">
              Verification Code
            </label>
            <input
              id="otp"
              name="otp"
              type="text"
              autoComplete="one-time-code"
              placeholder="Enter 6-digit code"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-menioo-green focus:border-menioo-green text-lg tracking-widest text-center"
              maxLength={6}
              required
            />
          </div>
          
          <div className="flex justify-between items-center">
            <div className="text-sm">
              {timerExpired ? (
                <button
                  type="button"
                  onClick={sendOTP}
                  disabled={isResending}
                  className="text-menioo-green hover:text-menioo-green-dark transition-colors"
                >
                  {isResending ? 'Sending...' : 'Resend Code'}
                </button>
              ) : (
                <span className="text-gray-500">
                  Resend code in {formatTime(remainingTime)}
                </span>
              )}
            </div>
            <button
              type="button"
              onClick={onCancel}
              className="text-gray-500 hover:text-gray-700 transition-colors"
            >
              Cancel
            </button>
          </div>
          
          <button
            type="submit"
            disabled={isLoading || otp.length !== 6}
            className="w-full py-2 px-4 border border-transparent rounded-md shadow-sm text-white bg-menioo-green hover:bg-menioo-green-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-menioo-green transition-colors disabled:opacity-50"
          >
            {isLoading ? (
              <div className="flex justify-center items-center">
                <Loader className="h-5 w-5 mr-2 animate-spin" />
                Verifying...
              </div>
            ) : (
              'Verify'
            )}
          </button>
        </form>
      </div>
    </div>
  );
}